<template>
  <ion-page>
    <ion-content :fullscreen="true">
      <!-- Two bands, same as the redeem screen: the message takes the room it
           needs and stays optically centred; the action sits on the bottom edge
           where a thumb already is. -->
      <div class="flex min-h-full flex-col bg-brown-50 px-5 pb-6 pt-8">
        <div
          class="mx-auto flex w-full max-w-[360px] flex-1 flex-col items-center justify-center text-center"
        >
          <!-- Working. No course name yet: which part this code unlocks lives on
               the token's row, so we do not know it until the server answers, and
               guessing then correcting reads worse than waiting a beat. -->
          <template v-if="state === 'loading'">
            <div class="mb-5 flex h-20 w-20 items-center justify-center">
              <DsSpinner size="xl" label="Unlocking your course" class="text-grey-60" />
            </div>
            <div class="mb-2 text-24-lh-24 font-black tracking-tight text-grey-800">
              Unlocking your course
            </div>
            <p class="text-text-body-lg font-medium text-grey-60">This only takes a moment.</p>
          </template>

          <template v-else-if="state === 'unlocked'">
            <div
              class="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-scanner-success/15 text-scanner-success"
            >
              <DsIcon name="check" size="xl" />
            </div>
            <div class="mb-2 text-24-lh-24 font-black tracking-tight text-grey-800">
              {{ unlockedHeadline }}
            </div>
            <p class="text-text-body-lg font-medium text-grey-60">
              It has been added to your account.
            </p>
          </template>

          <template v-else>
            <div
              class="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-error text-white"
            >
              <DsIcon name="close" size="xl" />
            </div>
            <div class="mb-2 text-24-lh-24 font-black tracking-tight text-grey-800">
              We couldn’t unlock this
            </div>
            <p class="text-text-body-lg font-medium text-grey-60">{{ errorMessage }}</p>
          </template>
        </div>

        <div
          v-if="state !== 'loading'"
          class="mx-auto w-full max-w-[360px] pb-[env(safe-area-inset-bottom)] pt-6"
        >
          <DsButton
            v-if="state === 'unlocked'"
            class="w-full"
            variant="secondary"
            size="56"
            shape="pill"
            @click="goToCourse"
          >
            Let’s go!
          </DsButton>
          <DsButton
            v-else
            class="w-full"
            variant="secondary"
            size="56"
            shape="pill"
            @click="goToCourses"
          >
            Back to Next Steps
          </DsButton>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
// Where a scanned LEADER unlock QR lands.
//
// Modelled on courses/redeem.vue, deliberately and closely. That screen exists
// because routing a camera scan through /scanner — a camera UI — while a grant the
// member could not see ran underneath was disorienting. This lane made the same
// mistake and produced three device bugs in one session: the scanner surface
// flashing at members who do not even have that feature, a history entry that
// re-ran the grant on Back, and a white page. So this page owns the moment
// instead, and the scanner is not involved at all.
//
// Three states, one job each, and the member always leaves by a button rather
// than being thrown somewhere.
//
// NOTE ON THE TOKEN IN THE URL: it is there because the pending deep-link has to
// survive an Auth0 round trip through localStorage. The token is single-use and
// grants a paid program for free, so it is a bearer instrument — `/courses/unlock`
// is registered in `shouldBlockTracking` for the same reason `/courses/redeem` is.

import { IonPage, IonContent } from '@ionic/vue';
import {
  Button as DsButton,
  Icon as DsIcon,
  Spinner as DsSpinner,
} from '@team-hogc/hogc-one-design-system';
import { hasLinkedCg } from '@/utils/cg-status';
import privateRegion from '@/mixins/PrivateRegion';

const ALL_TAB_PATH = '/tabs/all';
const DEFAULT_ERROR = 'This code didn’t work. Ask your leader for a new one.';
const CG_CHECK_FAILED = 'We couldn’t check your Connect Group. Try again in a moment.';
/** The API's refusal when the scanner has no live Connect Group. */
const MEMBER_HAS_NO_CG = 'MEMBER_HAS_NO_CG';

export default {
  name: 'CourseLeaderUnlockPage',
  mixins: [privateRegion],
  components: { IonPage, IonContent, DsButton, DsIcon, DsSpinner },
  data() {
    return {
      state: 'loading',
      programSlug: null,
      errorMessage: DEFAULT_ERROR,
    };
  },
  computed: {
    // The name arrives with the access refresh the redemption already triggers.
    // Falls back to the generic noun rather than naming nothing — both halves end
    // in "is unlocked" so the two read as the same sentence.
    unlockedHeadline() {
      const program = this.$store.getters['courses/programAccess']?.(this.programSlug);
      return program?.name ? `${program.name} is unlocked` : 'Your course is unlocked';
    },
  },
  async mounted() {
    await this.unlock();
  },
  methods: {
    async unlock() {
      const token = this.$route.params.token || '';
      if (!token) {
        this.state = 'error';
        return;
      }

      // The Connect Group gate, kept from the scanner lane rather than dropped
      // with it. A member the church cannot place is the reason it exists, and it
      // applies whoever handed out the code. Checked BEFORE the grant so the
      // member is not unlocked and then asked — and it fails CLOSED (owner
      // decision 2026-09-03): a profile that cannot be read unlocks nobody. The
      // server makes the same check and refuses with MEMBER_HAS_NO_CG, so this
      // copy is what routes the member to the chooser, not what protects the grant.
      const cg = await this.cgLinkStatus();
      if (cg === 'unknown') {
        this.errorMessage = CG_CHECK_FAILED;
        this.state = 'error';
        return;
      }
      if (cg === 'missing') {
        this.parkForCgLink(token);
        return;
      }

      try {
        const access = await this.$store.dispatch('courses/redeemLeaderUnlock', token);
        this.programSlug = access?.programSlug || null;
        this.state = 'unlocked';
      } catch (error) {
        // The server's own CG check disagreed with the profile we read — send
        // them to link one, exactly as the local check would have.
        if (error?.response?.data?.code === MEMBER_HAS_NO_CG) {
          this.parkForCgLink(token);
          return;
        }
        // 409 is the one worth naming: a code is good for ONE member, so this is
        // not retryable and "try again" would send them round the same loop.
        this.errorMessage =
          error?.response?.status === 409
            ? 'This code has already been used. Ask your leader for a new one.'
            : DEFAULT_ERROR;
        this.state = 'error';
      }
    },
    // Hand the token over BEFORE navigating, so the chooser raises the CG block
    // and, once a CG is linked, comes straight back here to finish the unlock
    // (CgHardBlock.onCgLinked). Without this the member was told to link a CG
    // and then had to find the leader's screen and scan again — the same reason
    // the redeem lane parks its card code.
    parkForCgLink(token) {
      this.$store.commit('courses/setCgPendingUnlockToken', token);
      this.$router.replace('/courses');
    },
    /**
     * 'linked' | 'missing' | 'unknown'. Three answers, not a boolean: "could not
     * tell" must not collapse into either of the other two, because each of
     * those routes somewhere and this one has to stop.
     */
    async cgLinkStatus() {
      try {
        await this.$store.dispatch('profile/fetchProfile');
      } catch (error) {
        console.warn('[Unlock] Could not resolve profile for CG check:', error?.message);
        return 'unknown';
      }
      return hasLinkedCg(this.$store.getters['profile/cgName']) ? 'linked' : 'missing';
    },
    goToCourse() {
      // replace, not push: the member must never land back on a grant that has
      // already happened by swiping back.
      this.$router.replace(this.programSlug ? `/courses/${this.programSlug}` : '/courses');
    },
    goToCourses() {
      this.$router.replace(
        this.$store.getters['courses/programVisible']?.('next-steps-p1')
          ? '/courses'
          : ALL_TAB_PATH,
      );
    },
  },
};
</script>

<style scoped>
ion-content {
  --background: theme('colors.brown.50');
}
</style>
